/** Виджет EditorLeftToolPanel: команды слева и выдвижная панель выбранного раздела. */
import { useState } from "react";
import { EDITOR_OVERLAY_PAINT_STYLE } from "@/shared/config/layout";
import { useProjectStore } from "@/shared/store";
import { UIKit } from "@/shared/ui";
import { DrawerSection, Tool, ViewMode, useEditorStore } from "@/widgets/editor";
import { FinishCatalogDrawer } from "@/widgets/finish-catalog";
import { LayersPanelContent } from "@/widgets/layers-panel";
import { ProjectPanelContent } from "@/widgets/properties-panel";
import { CatalogDrawer } from "./CatalogDrawer";
import { OpeningsDrawer } from "./OpeningsDrawer";
import { WallsDrawer } from "./WallsDrawer";
import { WaterDrawer } from "./WaterDrawer";
import { WiringDrawer } from "./WiringDrawer";
import { elevationButtons, planButtons, threeDButtons, type EditorToolRailItem } from "./buttons";

const DrawerContent = ({
	drawer,
	projectId,
}: {
	drawer: DrawerSection;
	projectId: string | null;
}) => {
	if (drawer === DrawerSection.Fixtures) return <CatalogDrawer />;
	if (drawer === DrawerSection.Finishes) return <FinishCatalogDrawer />;

	return (
		<div className="flex h-full flex-col gap-2.5 overflow-y-auto p-3">
			{drawer === DrawerSection.Walls && <WallsDrawer />}
			{drawer === DrawerSection.Openings && <OpeningsDrawer />}
			{drawer === DrawerSection.Wiring && <WiringDrawer />}
			{drawer === DrawerSection.Water && <WaterDrawer />}
			{drawer === DrawerSection.Layers && <LayersPanelContent />}
			{drawer === DrawerSection.Project && <ProjectPanelContent projectId={projectId} />}
		</div>
	);
};

export const EditorLeftToolPanel = ({
	projectId,
	onSelectProject,
}: {
	projectId: string | null;
	onSelectProject: () => void;
}) => {
	const tool = useEditorStore((state) => state.tool);
	const setTool = useEditorStore((state) => state.setTool);
	const drawer = useEditorStore((state) => state.drawer);
	const setDrawer = useEditorStore((state) => state.setDrawer);
	const isPlannerLeftPanelWide = useEditorStore((state) => state.isPlannerLeftPanelWide);
	const togglePlannerLeftPanelWide = useEditorStore((state) => state.togglePlannerLeftPanelWide);
	const viewMode = useEditorStore((state) => state.viewMode);
	const isReadOnly = useEditorStore((state) => state.isReadOnly);
	const planStage = useEditorStore((state) => state.planStage);
	const view3d = useEditorStore((state) => state.view3d);
	const setView3d = useEditorStore((state) => state.setView3d);
	const resetView3d = useEditorStore((state) => state.resetView3d);
	const showDimensions = useEditorStore((state) => state.showDimensions);
	const showAreas = useEditorStore((state) => state.showAreas);
	const toggleDimensions = useEditorStore((state) => state.toggleDimensions);
	const toggleAreas = useEditorStore((state) => state.toggleAreas);
	const showElevationRoomMap = useEditorStore((state) => state.showElevationRoomMap);
	const toggleElevationRoomMap = useEditorStore((state) => state.toggleElevationRoomMap);
	const projectView3d = useProjectStore((state) => state.project.view3d);
	const setProjectView3d = useProjectStore((state) => state.setView3d);

	const drawerContext = {
		drawer,
		isReadOnly,
		onToggleDrawer: (section: Exclude<DrawerSection, null>) =>
			setDrawer(drawer === section ? null : section),
	};
	const onSelect = () => {
		setTool(Tool.Select);
		setDrawer(null);
	};
	const planRailItems = planButtons({
		...drawerContext,
		tool,
		planStage,
		showDimensions,
		showAreas,
		onSelect,
		onToggleDimensions: toggleDimensions,
		onToggleAreas: toggleAreas,
		onSelectProject,
	});
	const elevationRailItems = elevationButtons({
		...drawerContext,
		tool,
		showDimensions,
		showElevationRoomMap,
		onSelect,
		onToggleDimensions: toggleDimensions,
		onToggleElevationRoomMap: toggleElevationRoomMap,
		onSelectProject,
	});
	const threeDRailItems = threeDButtons({
		...drawerContext,
		isReadOnly,
		...view3d,
		shadows: projectView3d.shadows,
		sourceLights: projectView3d.sourceLights,
		onToggleCutaway: () => setView3d({ cutaway: !view3d.cutaway }),
		onToggleRoomMap: () => setView3d({ roomMap: !view3d.roomMap }),
		onToggleSunControl: () => setView3d({ sunControl: !view3d.sunControl }),
		onToggleCameraMovePad: () => setView3d({ cameraMovePad: !view3d.cameraMovePad }),
		onToggleFirstPerson: () => setView3d({ firstPerson: !view3d.firstPerson }),
		onToggleShadows: () => setProjectView3d({ shadows: !projectView3d.shadows }),
		onToggleSourceLights: () => setProjectView3d({ sourceLights: !projectView3d.sourceLights }),
		onToggleGrid: () => setView3d({ grid: !view3d.grid }),
		onResetView: resetView3d,
	});
	const menus: Record<ViewMode, EditorToolRailItem[]> = {
		[ViewMode.TwoD]: planRailItems,
		[ViewMode.Elevation]: elevationRailItems,
		[ViewMode.ThreeD]: threeDRailItems,
		[ViewMode.Split]: planRailItems,
		// В смете левой панели нет — это лист во всю рабочую область
		[ViewMode.Estimate]: [],
	};
	const railItems = menus[viewMode];
	const activeSection = railItems.find((item) => item.drawer === drawer);
	const isWideCatalog = drawer === DrawerSection.Fixtures || drawer === DrawerSection.Finishes;
	// В «Отделке» рейка складывается до иконок — место уходит образцам; закрыли «Отделку» —
	// рейка снова такая, как её оставил пользователь. Раскрыть её внутри «Отделки» тоже можно
	const inFinishes = drawer === DrawerSection.Finishes;
	const [finishesLabels, setFinishesLabels] = useState(false);
	if (!inFinishes && finishesLabels) setFinishesLabels(false);

	return (
		<UIKit.LeftPanel
			railKey={viewMode}
			items={railItems}
			labelsVisible={inFinishes ? finishesLabels : isPlannerLeftPanelWide}
			onToggleLabels={
				inFinishes ? () => setFinishesLabels((visible) => !visible) : togglePlannerLeftPanelWide
			}
			style={EDITOR_OVERLAY_PAINT_STYLE}
			drawerClassName={
				// Отделка и каталог предметов — карточками с картинками, им нужна ширина
				isWideCatalog ? "md:w-96 lg:w-lg" : "md:w-80"
			}
			drawer={
				drawer === null
					? null
					: {
							label: activeSection?.label ?? "",
							content: <DrawerContent drawer={drawer} projectId={projectId} />,
						}
			}
			onCloseDrawer={() => setDrawer(null)}
		/>
	);
};
